import type { RequestFn } from '../http';

/** Admin → Mail-Eingang (Schattenbetrieb). Backend: routes/mailIntake.ts */

export type MailIntakeAction = 'CREATE_TICKET' | 'APPEND_TO_TICKET' | 'IGNORE' | 'REVIEW';
export type MailIntakeAgreement = 'PENDING' | 'MATCH' | 'INTENDED' | 'CHECK' | 'MISMATCH';
export type MailIntakeMatchVia = 'MESSAGE_ID' | 'SUBJECT_NUMBER' | 'CONTENT';
export type MailIntakeKind = 'NEW_REQUEST' | 'REPLY' | 'AUTO_REPLY' | 'BOUNCE' | 'BULK' | 'LOOP' | 'INTERNAL';

export interface MailIntakeSettings {
  enabled: boolean;
  mailbox: string;
  folder: string;
}

export interface MailIntakeOverview {
  /** Es gibt nur diesen Modus — scharf schalten ist Code, keine Einstellung. */
  mode: 'SHADOW';
  settings: MailIntakeSettings;
  state: {
    cursor: string | null;
    lastRunAt: string | null;
    lastOkAt: string | null;
    lastError: string | null;
    lastStored: number;
  };
  summary: {
    since: string;
    total: number;
    byAction: Partial<Record<MailIntakeAction, number>>;
    byAgreement: Partial<Record<MailIntakeAgreement, number>>;
    agreementRate: number | null;
  };
  retentionDays: number;
  /** Microsoft-App mit Secret hinterlegt (ob sie Mail.Read hat, zeigt erst ein Abruf) */
  microsoftConfigured: boolean;
  /** Eigene App nur für den Mail-Eingang (Backend: services/mailIntake/mailIntakeApp.ts) */
  intakeApp: MailIntakeAppStatus;
  setupIssue: MailIntakeSetupIssue | null;
}

export interface MailIntakeScopeCheck {
  checkedAt: string;
  /** true = nur dieses Postfach, false = liest auch andere, null = nicht feststellbar */
  restricted: boolean | null;
  detail: string;
}

/** Begrenzung, die der ServiceHub per Gerätecode in Exchange gesetzt hat */
export interface MailIntakeRestriction {
  configuredAt: string;
  mailbox: string;
  configuredBy: string;
  policyIdentity: string | null;
}

export interface MailIntakeAppStatus {
  configured: boolean;
  displayName: string;
  clientId: string | null;
  createdAt: string | null;
  secretExpiresAt: string | null;
  scopeCheck: MailIntakeScopeCheck | null;
  restriction: MailIntakeRestriction | null;
  /** Exchange-Befehle zum Begrenzen — erst, wenn App und Postfach feststehen */
  restrictCommands: string | null;
}

export type MailIntakeAppSetupPoll =
  | { status: 'pending'; pollIntervalMs: number }
  | { status: 'ok'; steps: string[] }
  | { status: 'error'; message: string };

/** Was noch fehlt — dieselbe Antwort für Admin-Hinweis und Einstellungs-Reiter. */
export interface MailIntakeSetupIssue {
  step: 'MICROSOFT' | 'MAILBOX' | 'INACTIVE' | 'ERROR';
  message: string;
}

export interface InboundMailListItem {
  id: string;
  mailbox: string;
  internetMessageId: string | null;
  inReplyTo: string | null;
  fromAddress: string | null;
  fromName: string | null;
  toAddresses: string[];
  ccAddresses: string[];
  subject: string | null;
  receivedAt: string;
  attachments: Array<{ name: string; contentType: string | null; size: number; isInline: boolean }> | null;
  kind: MailIntakeKind;
  plannedAction: MailIntakeAction;
  plannedReason: string;
  plannedTicketId: string | null;
  plannedTicketNumber: string | null;
  ticketMatchVia: MailIntakeMatchVia | null;
  /** Inhaltsabgleich gegen Tickets der letzten 7 Tage — auch ohne Treffer */
  contentMatch: {
    candidates: number;
    ticketId: string | null;
    ticketNumber: string | null;
    confidence: number | null;
    reasoning: string | null;
    error: string | null;
  } | null;
  plannedCustomerId: string | null;
  plannedCustomerName: string | null;
  customerMatch: string | null;
  plannedReplies: string[];
  weclappOutcome: 'TICKET_CREATED' | 'APPENDED' | 'NOTHING' | null;
  weclappTicketId: string | null;
  weclappTicketNumber: string | null;
  weclappMatchedBy: 'MESSAGE_ID' | 'HEURISTIC' | null;
  agreement: MailIntakeAgreement;
  agreementNote: string | null;
}

export interface InboundMailDetail extends InboundMailListItem {
  bodyPreview: string | null;
  headers: Record<string, string> | null;
  referenceIds: string[];
}

export function createMailIntakeApi(request: RequestFn) {
  return {
    async getMailIntake() {
      return request<MailIntakeOverview>('/admin/mail-intake');
    },

    async getMailIntakeSetupStatus() {
      return request<{ setupIssue: MailIntakeSetupIssue | null }>('/admin/mail-intake/setup-status');
    },

    async saveMailIntakeSettings(body: MailIntakeSettings) {
      return request<{ settings: MailIntakeSettings }>('/admin/mail-intake/settings', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },

    /** Verbindung mit den Formularwerten prüfen — auch ungespeichert. */
    async testMailIntake(body: { mailbox: string; folder: string }) {
      return request<{ ok: true; displayName: string; totalItemCount: number }>('/admin/mail-intake/test', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    /** Eigene App per Gerätecode anlegen — Mail.Read + Admin-Zustimmung. */
    async startMailIntakeAppSetup() {
      return request<{ sessionId: string; verificationUriComplete: string; userCode: string; pollIntervalMs: number }>(
        '/admin/mail-intake/app-setup',
        { method: 'POST', body: '{}' }
      );
    },

    async pollMailIntakeAppSetup(sessionId: string) {
      return request<MailIntakeAppSetupPoll>(`/admin/mail-intake/app-setup/poll?sessionId=${encodeURIComponent(sessionId)}`);
    },

    /** App in Exchange auf das Postfach begrenzen — Gerätecode, Anmeldung als Exchange-Admin. */
    async startMailIntakeRestrictSetup() {
      return request<{ sessionId: string; verificationUriComplete: string; userCode: string; pollIntervalMs: number }>(
        '/admin/mail-intake/restrict-setup',
        { method: 'POST', body: '{}' }
      );
    },

    async pollMailIntakeRestrictSetup(sessionId: string) {
      return request<MailIntakeAppSetupPoll>(
        `/admin/mail-intake/restrict-setup/poll?sessionId=${encodeURIComponent(sessionId)}`
      );
    },

    async checkMailIntakeAppScope() {
      return request<{ scopeCheck: MailIntakeScopeCheck }>('/admin/mail-intake/app-scope-check', { method: 'POST', body: '{}' });
    },

    async runMailIntake() {
      return request<{ result: { fetched: number; stored: number; reconciled: number } }>('/admin/mail-intake/run', {
        method: 'POST',
      });
    },

    async listInboundMails(params: {
      agreement?: MailIntakeAgreement;
      action?: MailIntakeAction;
      via?: MailIntakeMatchVia;
      page?: number;
    }) {
      const q = new URLSearchParams();
      if (params.via) q.set('via', params.via);
      if (params.agreement) q.set('agreement', params.agreement);
      if (params.action) q.set('action', params.action);
      if (params.page) q.set('page', String(params.page));
      return request<{ items: InboundMailListItem[]; total: number; page: number; pageSize: number }>(
        `/admin/mail-intake/mails?${q.toString()}`
      );
    },

    async getInboundMail(id: string) {
      return request<{ mail: InboundMailDetail }>(`/admin/mail-intake/mails/${id}`);
    },
  };
}
